import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [principal, setPrincipal] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setPrincipal(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setPrincipal(data);
      return data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setPrincipal(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyToken = (payload) => {
    localStorage.setItem(TOKEN_KEY, payload.access_token);
    setPrincipal(payload.principal);
    return payload.principal;
  };

  const value = useMemo(
    () => ({
      principal,
      loading,
      refresh,
      async loginUser(email, password) {
        const { data } = await api.post("/auth/login", { email, password });
        return applyToken(data);
      },
      async registerParent(body) {
        const { data } = await api.post("/auth/parent/register", body);
        return applyToken(data);
      },
      async loginGoogle(credential) {
        const { data } = await api.post("/auth/google", { credential });
        return applyToken(data);
      },
      async acceptInvitation(token, body) {
        const { data } = await api.post(`/auth/invitations/${token}/accept`, body);
        return applyToken(data);
      },
      async loginStudent(body) {
        const { data } = await api.post("/auth/student/login", body);
        return applyToken(data);
      },
      logout() {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          api
            .post("/auth/logout", null, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => {});
        }
        localStorage.removeItem(TOKEN_KEY);
        setPrincipal(null);
      },
    }),
    [principal, loading, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
